import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlRadioButton,
  Radio,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const EmailManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedLesson, setSelectedLesson] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [lessonTab, setLessonTab] = useState(0); // 0 = upcoming, 1 = past

  // Data state
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [pastLessons, setPastLessons] = useState([]);
  const [preview, setPreview] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Quill editor modules
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
  ];

  // Fetch lessons on mount
  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    setLessonsLoading(true);
    try {
      const [upcomingRes, pastRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/emails/lessons/upcoming`),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/emails/lessons/past`),
      ]);

      if (upcomingRes.ok) {
        const upcomingData = await upcomingRes.json();
        setUpcomingLessons(upcomingData);
      }

      if (pastRes.ok) {
        const pastData = await pastRes.json();
        setPastLessons(pastData);
      }
    } catch (err) {
      console.error("Error fetching lessons:", err);
      setError("Nie udało się pobrać listy zajęć");
    } finally {
      setLessonsLoading(false);
    }
  };

  const handlePreview = async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/emails/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lesson_id: parseInt(selectedLesson),
        }),
      });

      if (!response.ok) {
        throw new Error("Nie udało się pobrać podglądu odbiorców");
      }

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    // Validate form
    if (!subject.trim()) {
      setError("Temat wiadomości jest wymagany");
      return;
    }
    if (!body.trim() || body === "<p><br></p>") {
      setError("Treść wiadomości jest wymagana");
      return;
    }
    if (!selectedLesson) {
      setError("Proszę wybrać zajęcia");
      return;
    }

    setConfirmDialogOpen(true);
  };

  const confirmSend = async () => {
    setConfirmDialogOpen(false);
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/emails/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject,
          body,
          lesson_id: parseInt(selectedLesson),
          test_mode: testMode,
        }),
      });

      if (!response.ok) {
        throw new Error("Nie udało się wysłać wiadomości");
      }

      const data = await response.json();
      setSuccess(data.message);

      // Reset form after successful send
      if (!testMode) {
        setSubject("");
        setBody("");
        setSelectedLesson("");
        setPreview(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentLessons = lessonTab === 0 ? upcomingLessons : pastLessons;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Wysyłka E-mail
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Lesson Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
              Wybierz zajęcia
            </Typography>
              <Tabs
                value={lessonTab}
                onChange={(e, newValue) => {
                  setLessonTab(newValue);
                  setSelectedLesson("");
                  setPreview(null);
                }}
                sx={{ mb: 2 }}
              >
                <Tab label="Nadchodzące zajęcia" />
                <Tab label="Przeszłe zajęcia" />
              </Tabs>

              <FormControl fullWidth>
                <Select
                  value={selectedLesson}
                  onChange={(e) => {
                    setSelectedLesson(e.target.value);
                    setPreview(null);
                  }}
                  displayEmpty
                  disabled={lessonsLoading}
                >
                  <MenuItem value="" disabled>
                    {lessonsLoading
                      ? "Ładowanie..."
                      : "Wybierz zajęcia"}
                  </MenuItem>
                  {currentLessons.map((lesson) => (
                    <MenuItem key={lesson.id} value={lesson.id}>
                      <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
                        <Typography variant="body1">
                          {lesson.friendly_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(lesson.start).toLocaleString("pl-PL", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {" • "}
                          {lesson.participant_count} uczestników
                          {lesson.location_name && ` • ${lesson.location_name}`}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
          </Box>

          {/* Subject Field */}
          <TextField
            label="Temat"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 3 }}
            placeholder="Wpisz temat wiadomości..."
          />

          {/* Body Editor */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              Treść wiadomości
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Wiadomość zostanie automatycznie uzupełniona o powitanie z imieniem użytkownika i podpis studia.
            </Typography>
            <Box
              sx={{
                "& .quill": {
                  backgroundColor: "white",
                },
                "& .ql-container": {
                  minHeight: isMobile ? "200px" : "300px",
                  fontSize: "14px",
                },
                "& .ql-editor": {
                  minHeight: isMobile ? "200px" : "300px",
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={body}
                onChange={setBody}
                modules={modules}
                formats={formats}
                placeholder="Napisz treść wiadomości..."
              />
            </Box>
          </Box>

          {/* Test Mode */}
          <FormControlLabel
            control={
              <Checkbox
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Tryb testowy</Typography>
                <Typography variant="caption" color="text.secondary">
                  Wyślij tylko do administratora (nie do rzeczywistych odbiorców)
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          {/* Preview Section */}
          {preview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                {preview.message}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Liczba odbiorców: <strong>{preview.recipient_count}</strong>
              </Typography>
              {preview.recipients_preview.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                  {preview.recipients_preview.map((email, index) => (
                    <Chip key={index} label={email} size="small" />
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={loading || !selectedLesson}
              fullWidth={isMobile}
            >
              Podgląd odbiorców
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={loading}
              fullWidth={isMobile}
              sx={{
                backgroundColor: testMode ? "#ff9800" : "#667eea",
                "&:hover": {
                  backgroundColor: testMode ? "#f57c00" : "#5568d3",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : testMode ? (
                "Wyślij Test"
              ) : (
                "Wyślij Email"
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          {testMode ? "Potwierdzenie wysyłki testowej" : "Potwierdzenie wysyłki"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {testMode
              ? "Czy na pewno chcesz wysłać testową wiadomość do administratora?"
              : preview
              ? `Czy na pewno chcesz wysłać wiadomość do ${preview.recipient_count} odbiorców?`
              : "Czy na pewno chcesz wysłać tę wiadomość?"}
          </Typography>
          {testMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Wiadomość zostanie wysłana tylko na adres administratora z informacją o rzeczywistych odbiorcach.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Anuluj</Button>
          <Button
            onClick={confirmSend}
            variant="contained"
            color={testMode ? "warning" : "primary"}
          >
            Potwierdź
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailManagement;
